import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  AdminAuctionSale,
  PatchAdminV1StoresStoreIdSalesIdBody,
  PostAdminV1StoresStoreIdSalesBody,
} from '@nucleus-commerce/api-client/admin';

import { salesApi } from '../api/sales-api';

import { auctionKeys } from './auction-keys';

/**
 * The answer is the whole sale: it replaces the detail, and the sale lists refetch. Scheduling,
 * cancelling and lot changes also touch the lots themselves, so every auction query refetches.
 */
async function storeSale(
  queryClient: QueryClient,
  storeId: string,
  sale: AdminAuctionSale,
  lotsChanged: boolean,
) {
  queryClient.setQueryData(auctionKeys.sale(storeId, sale.id), sale);
  await queryClient.invalidateQueries({ queryKey: auctionKeys.saleLists(storeId) });
  if (lotsChanged) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: auctionKeys.lists(storeId) }),
      queryClient.invalidateQueries({ queryKey: auctionKeys.details(storeId) }),
      queryClient.invalidateQueries({ queryKey: auctionKeys.saleLots(storeId, sale.id) }),
    ]);
  }
}

export function useCreateSale(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdSalesBody) => salesApi.create(storeId, body),
    meta: { successMessage: 'auctions:sales.toasts.created', silentError: true },
    onSuccess: (sale) => storeSale(queryClient, storeId, sale, false),
  });
}

export function useUpdateSale(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdSalesIdBody }) =>
      salesApi.update(storeId, id, body),
    meta: { successMessage: 'auctions:sales.toasts.updated', silentError: true },
    onSuccess: (sale) => storeSale(queryClient, storeId, sale, false),
  });
}

export function useScheduleSale(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => salesApi.schedule(storeId, id),
    meta: { successMessage: 'auctions:sales.toasts.scheduled', silentError: true },
    onSuccess: (sale) => storeSale(queryClient, storeId, sale, true),
  });
}

export function useCancelSale(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      salesApi.cancel(storeId, id, reason),
    meta: { successMessage: 'auctions:sales.toasts.cancelled', silentError: true },
    onSuccess: (sale) => storeSale(queryClient, storeId, sale, true),
  });
}

export function useAddSaleLot(storeId: string, saleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (auctionId: string) => salesApi.addLot(storeId, saleId, auctionId),
    meta: { successMessage: 'auctions:sales.toasts.lotAdded', silentError: true },
    onSuccess: async (sale) => {
      await storeSale(queryClient, storeId, sale, true);
      await queryClient.invalidateQueries({ queryKey: auctionKeys.looseDrafts(storeId) });
    },
  });
}

export function useReorderSaleLots(storeId: string, saleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (auctionIds: string[]) => salesApi.reorderLots(storeId, saleId, auctionIds),
    meta: { successMessage: 'auctions:sales.toasts.reordered' },
    onSuccess: (sale) => storeSale(queryClient, storeId, sale, true),
  });
}

export function useRemoveSaleLot(storeId: string, saleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (auctionId: string) => salesApi.removeLot(storeId, saleId, auctionId),
    meta: { successMessage: 'auctions:sales.toasts.lotRemoved' },
    onSuccess: async (sale) => {
      await storeSale(queryClient, storeId, sale, true);
      await queryClient.invalidateQueries({ queryKey: auctionKeys.looseDrafts(storeId) });
    },
  });
}
